import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import {
  DeviceIds,
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  ScheduleCommands,
  ScheduleProperties,
  TemperatureLevelsPerceivedCommands,
  TemperatureLevelsPerceivedPrograms,
  TemperatureProperty,
} from '@vi-iot/client';

import { FeatureService, InstallationFeaturesStore } from '@store';

import {
  ScheduleEntry,
  ScheduleService,
  TimemanagerDialogService,
} from '../../../features/timemanager/services/index';
import { GTMService } from '../../../shared/services';

@Component({
  selector: 'app-src-heating-panel',
  templateUrl: './src-heating-panel.component.html',
  styleUrls: ['./src-heating-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SrcHeatingPanelComponent implements OnInit, OnDestroy {
  heatingSchedule$: Observable<Feature<ScheduleProperties, ScheduleCommands>>;
  temperatureLevelsModes: Feature<TemperatureProperty, TemperatureLevelsPerceivedCommands>[];

  ngUnsubscribe: Subject<void> = new Subject<void>();

  @Input() roomId: string;

  constructor(
    private featureService: FeatureService,
    private scheduleService: ScheduleService,
    private timemanagerDialogService: TimemanagerDialogService,
    private gtmService: GTMService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.heatingSchedule$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomSchedule(this.roomId),
      DeviceIds.RoomControl
    );
    this.initTemperatureModesTable();
    this.initHeatingModes();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initHeatingModes(): void {
    this.heatingSchedule$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => !!feature?.properties?.entries?.value),
        tap((feature) => {
          this.scheduleService.createSchedule(feature);
        }),
        map((feature) => this.featureService.getScheduleModes(feature)),
        tap((modes) => this.scheduleService.createModes(modes)),
        map((modes) => this.scheduleService.supportHeatingPrograms(modes)),
        map((modes) =>
          modes.map((mode) =>
            this.installationFeaturesStore.selectInstallationDeviceFeature(
              FeatureNames.roomQuickModesProgram(this.roomId, mode.name)
            )
          )
        )
      )
      .subscribe();
  }

  initTemperatureModesTable(): void {
    combineLatest([
      this.installationFeaturesStore.selectInstallationDeviceFeature<
        TemperatureProperty,
        TemperatureLevelsPerceivedCommands
      >(
        FeatureNames.roomTemperatureLevelsPerceived(
          this.roomId,
          TemperatureLevelsPerceivedPrograms.normal
        ),
        DeviceIds.RoomControl
      ),
      this.installationFeaturesStore.selectInstallationDeviceFeature<
        TemperatureProperty,
        TemperatureLevelsPerceivedCommands
      >(
        FeatureNames.roomTemperatureLevelsPerceived(
          this.roomId,
          TemperatureLevelsPerceivedPrograms.reduced
        ),
        DeviceIds.RoomControl
      ),
      this.installationFeaturesStore.selectInstallationDeviceFeature<
        TemperatureProperty,
        TemperatureLevelsPerceivedCommands
      >(
        FeatureNames.roomTemperatureLevelsPerceived(
          this.roomId,
          TemperatureLevelsPerceivedPrograms.comfort
        ),
        DeviceIds.RoomControl
      ),
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((featureTable) => {
        this.temperatureLevelsModes = featureTable;
      });
  }

  onEditMultipleDays(): void {
    this.timemanagerDialogService.openSelectDaysDialog();
  }

  onProgramTemperatureChange(value: number, featureName: string): void {
    //taking mode name from rooms.N.temperature.levels.[reduced|normal|comfort].perceived
    const parts = featureName.split('.');
    const programName = parts[parts.length - 2];

    this.installationFeaturesStore.updateFeature(
      FeatureNames.roomTemperatureLevelsPerceived(this.roomId, programName),
      value,
      FeaturePropertyKey.temperature,
      FeatureActionFields.targetTemperature,
      FeatureCommands.setTemperature,
      null,
      DeviceIds.RoomControl
    );
    this.gtmService.track('onSet', `Room's ${programName} program temperature`, 'srcHeating');
  }

  onScheduleChange(
    event: { scheduleIds: number[]; periods: ScheduleEntry[] },
    feature: Feature<ScheduleProperties, ScheduleCommands>
  ): void {
    const schedule = this.scheduleService.getScheduleEntries(feature);
    const value = this.scheduleService.getScheduleValue(event.scheduleIds, event.periods, schedule);
    this.installationFeaturesStore.updateFeature(
      FeatureNames.roomSchedule(this.roomId),
      cloneDeep(value),
      FeaturePropertyKey.entries,
      FeatureActionFields.newSchedule,
      FeatureCommands.setSchedule,
      null,
      DeviceIds.RoomControl
    );
  }
}
